import { c as _c } from "react/compiler-runtime";
import type { AnnotatedBboxData, DocumentViewerCardContentBoundingBox, SimpleBboxData, TabGroupKind } from '@onefootprint/request-types/dashboard';
import { Truncate } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import capitalize from 'lodash/capitalize';
import React from 'react';
import { useTranslation } from 'react-i18next';
type BoundingBoxContentProps = {
  content: DocumentViewerCardContentBoundingBox;
  cardKind: TabGroupKind;
  selectedBboxIds: Set<string>;
  onBboxClick: (id: string, pageId?: number) => void;
  isEncrypted?: boolean;
};
const BoundingBoxContent = t0 => {
  const $ = _c(15);
  const {
    content,
    cardKind,
    selectedBboxIds,
    onBboxClick,
    isEncrypted
  } = t0;
  const {
    t
  } = useTranslation("document-viewer");
  const isExtraction = cardKind === "extraction";
  let t1;
  if ($[0] !== content.bboxGroups || $[1] !== isEncrypted || $[2] !== isExtraction || $[3] !== onBboxClick || $[4] !== selectedBboxIds || $[5] !== t) {
    let t2;
    if ($[7] !== isEncrypted || $[8] !== isExtraction || $[9] !== onBboxClick || $[10] !== selectedBboxIds || $[11] !== t) {
      t2 = group => <React.Fragment key={`${group.kind}-${group.bboxes.map(_temp).join("-")}`}>{group.kind === "annotated" && isExtraction && <ExtractionKeyValueList group={group} selectedBboxIds={selectedBboxIds} onBboxClick={onBboxClick} isEncrypted={isEncrypted} />}{group.kind === "annotated" && !isExtraction && <AnnotatedBboxTable group={group} selectedBboxIds={selectedBboxIds} onBboxClick={onBboxClick} isEncrypted={isEncrypted} />}{group.kind === "simple" && <SimpleBboxList bboxes={group.bboxes} selectedBboxIds={selectedBboxIds} onBboxClick={onBboxClick} pageHighlightLabel={page => t(_temp2, {
          page
        })} isEncrypted={isEncrypted} />}</React.Fragment>;
      $[7] = isEncrypted;
      $[8] = isExtraction;
      $[9] = onBboxClick;
      $[10] = selectedBboxIds;
      $[11] = t;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    t1 = content.bboxGroups.map(t2);
    $[0] = content.bboxGroups;
    $[1] = isEncrypted;
    $[2] = isExtraction;
    $[3] = onBboxClick;
    $[4] = selectedBboxIds;
    $[5] = t;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[13] !== t1) {
    t2 = <div className="flex flex-col gap-0.5">{t1}</div>;
    $[13] = t1;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
};
type ExtractionKeyValueListProps = {
  group: {
    kind: 'annotated';
    bboxes: AnnotatedBboxData[];
    metadataColumnsDefinition: Array<{
      columnName: string;
      fieldName: string;
    }>;
  };
  selectedBboxIds: Set<string>;
  onBboxClick: (id: string, pageId?: number) => void;
  isEncrypted?: boolean;
};
const getMetadataValue = (metadata: Record<string, unknown>, key: string): string => {
  const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
  return String(metadata[camelKey] ?? metadata[key] ?? '');
};
const ExtractionKeyValueList = t0 => {
  const $ = _c(11);
  const {
    group,
    selectedBboxIds,
    onBboxClick,
    isEncrypted
  } = t0;
  let t1;
  if ($[0] !== group.bboxes || $[1] !== isEncrypted || $[2] !== onBboxClick || $[3] !== selectedBboxIds) {
    let t2;
    if ($[5] !== isEncrypted || $[6] !== onBboxClick || $[7] !== selectedBboxIds) {
      t2 = bbox => {
        const hasBbox = bbox.bbox !== null && !isEncrypted;
        const isSelected = hasBbox && selectedBboxIds.has(bbox.id);
        const fieldName = capitalize(getMetadataValue(bbox.metadata, "field_name"));
        const rawValue = getMetadataValue(bbox.metadata, "value");
        const value = bbox.bbox !== null ? rawValue : "-";
        return <button key={bbox.id} type="button" onClick={hasBbox ? () => onBboxClick(bbox.id, bbox.pageId) : undefined} disabled={!hasBbox} className={cx("flex rounded-md gap-6 justify-between w-[calc(100%+16px)] px-2 py-2 -ml-2 border", {
          "border-accent bg-active": isSelected,
          "bg-primary border-transparent hover:bg-hover cursor-pointer": !isSelected && hasBbox,
          "bg-primary border-transparent cursor-default": !hasBbox
        })}><div className="text-caption-2 text-tertiary shrink-0">{fieldName}</div><Truncate className="text-caption-2 text-primary text-right min-w-0">{value}</Truncate></button>;
      };
      $[5] = isEncrypted;
      $[6] = onBboxClick;
      $[7] = selectedBboxIds;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    t1 = group.bboxes.map(t2);
    $[0] = group.bboxes;
    $[1] = isEncrypted;
    $[2] = onBboxClick;
    $[3] = selectedBboxIds;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[9] !== t1) {
    t2 = <>{t1}</>;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
};
type AnnotatedBboxTableProps = {
  group: {
    kind: 'annotated';
    bboxes: AnnotatedBboxData[];
    metadataColumnsDefinition: Array<{
      columnName: string;
      fieldName: string;
    }>;
  };
  selectedBboxIds: Set<string>;
  onBboxClick: (id: string, pageId?: number) => void;
  isEncrypted?: boolean;
};
const AnnotatedBboxTable = t0 => {
  const $ = _c(22);
  const {
    group,
    selectedBboxIds,
    onBboxClick,
    isEncrypted
  } = t0;
  let t1;
  if ($[0] !== group.metadataColumnsDefinition) {
    let t2;
    if ($[2] !== group.metadataColumnsDefinition.length) {
      t2 = (col, colIndex) => <div key={col.fieldName} className={cx("px-2 py-2 text-caption-1 text-tertiary", {
        "flex-1": colIndex < group.metadataColumnsDefinition.length - 1,
        "w-24 text-right": colIndex === group.metadataColumnsDefinition.length - 1
      })}>{col.columnName}</div>;
      $[2] = group.metadataColumnsDefinition.length;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    t1 = group.metadataColumnsDefinition.map(t2);
    $[0] = group.metadataColumnsDefinition;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <div className="flex border-b border-tertiary mb-1">{t1}</div>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== group.bboxes || $[7] !== group.metadataColumnsDefinition || $[8] !== isEncrypted || $[9] !== onBboxClick || $[10] !== selectedBboxIds) {
    let t4;
    if ($[12] !== group.metadataColumnsDefinition || $[13] !== isEncrypted || $[14] !== onBboxClick || $[15] !== selectedBboxIds) {
      t4 = bbox => {
        const hasBboxData = bbox.bbox !== null;
        const isSelectable = hasBboxData && !isEncrypted;
        const isSelected = isSelectable && selectedBboxIds.has(bbox.id);
        return <button key={bbox.id} type="button" onClick={isSelectable ? () => onBboxClick(bbox.id, bbox.pageId) : undefined} disabled={!isSelectable} className={cx("flex w-full transition-colors rounded-md border", {
          "border-accent bg-active": isSelected,
          "border-transparent hover:bg-hover cursor-pointer": !isSelected && isSelectable,
          "border-transparent cursor-default": !isSelectable
        })}>{group.metadataColumnsDefinition.map((col_0, colIndex_0) => {
            const isFirstColumn = colIndex_0 === 0;
            const isLastColumn = colIndex_0 === group.metadataColumnsDefinition.length - 1;
            const cellValue = hasBboxData ? getMetadataValue(bbox.metadata, col_0.fieldName) : "-";
            return <div key={col_0.fieldName} className={cx("px-2 py-2 text-left", {
              "flex-1 min-w-0": !isLastColumn,
              "w-24 text-right shrink-0": isLastColumn
            })}>{isFirstColumn && hasBboxData ? <Truncate className="text-caption-2 text-primary">{cellValue}</Truncate> : <span className="text-caption-2 text-primary">{cellValue}</span>}</div>;
          })}</button>;
      };
      $[12] = group.metadataColumnsDefinition;
      $[13] = isEncrypted;
      $[14] = onBboxClick;
      $[15] = selectedBboxIds;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    t3 = group.bboxes.map(t4);
    $[6] = group.bboxes;
    $[7] = group.metadataColumnsDefinition;
    $[8] = isEncrypted;
    $[9] = onBboxClick;
    $[10] = selectedBboxIds;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[17] !== t3) {
    t4 = <div className="flex flex-col gap-0.5">{t3}</div>;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== t2 || $[20] !== t4) {
    t5 = <div className="w-full">{t2}{t4}</div>;
    $[19] = t2;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  return t5;
};
type SimpleBboxListProps = {
  bboxes: SimpleBboxData[];
  selectedBboxIds: Set<string>;
  onBboxClick: (id: string, pageId?: number) => void;
  pageHighlightLabel: (page: number) => string;
  isEncrypted?: boolean;
};
const SimpleBboxList = t0 => {
  const $ = _c(13);
  const {
    bboxes,
    selectedBboxIds,
    onBboxClick,
    pageHighlightLabel,
    isEncrypted
  } = t0;
  let t1;
  if ($[0] !== bboxes || $[1] !== isEncrypted || $[2] !== onBboxClick || $[3] !== pageHighlightLabel || $[4] !== selectedBboxIds) {
    let t2;
    if ($[6] !== isEncrypted || $[7] !== onBboxClick || $[8] !== pageHighlightLabel || $[9] !== selectedBboxIds) {
      t2 = bbox => {
        const hasBboxData = bbox.bbox !== null;
        const isSelectable = hasBboxData && !isEncrypted;
        const isSelected = isSelectable && selectedBboxIds.has(bbox.id);
        return <button key={bbox.id} type="button" onClick={isSelectable ? () => onBboxClick(bbox.id, bbox.pageId) : undefined} disabled={!isSelectable} className={cx("text-left py-2 px-2 -ml-2 w-[calc(100%+16px)] text-caption-2 text-primary transition-colors rounded-md border", {
          "border-accent bg-active": isSelected,
          "border-transparent hover:bg-hover cursor-pointer": !isSelected && isSelectable,
          "border-transparent cursor-default": !isSelectable
        })}>{hasBboxData ? pageHighlightLabel((bbox.pageId ?? 0) + 1) : "-"}</button>;
      };
      $[6] = isEncrypted;
      $[7] = onBboxClick;
      $[8] = pageHighlightLabel;
      $[9] = selectedBboxIds;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    t1 = bboxes.map(t2);
    $[0] = bboxes;
    $[1] = isEncrypted;
    $[2] = onBboxClick;
    $[3] = pageHighlightLabel;
    $[4] = selectedBboxIds;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[11] !== t1) {
    t2 = <div className="flex flex-col gap-1">{t1}</div>;
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
};
export default BoundingBoxContent;
function _temp(bbox) {
  return bbox.id;
}
function _temp2($) {
  return $.boundingBox.pageHighlight;
}